import React, { useEffect, useMemo, useState } from "react";
import { clone, isEmpty, isNil } from "ramda";
import {
  asociarNumeroConLista,
  contarHastaIndice,
  createColor,
  getMeasure,
  shouldAddPoints,
  shouldAddPointsTotal,
} from "./utils";
import { MapContainer, TileLayer, Polyline, Popup, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import Legend from "../Gradient";
import { BOOLEAN_MEASURES } from "../../../../utils/consts";
import { useSelector } from "react-redux";
import { Button, Dialog, DialogActions, DialogContent } from "@mui/material";

const BoundsFitter = ({ assetData }) => {
  const map = useMap();

  useEffect(() => {
    if (!isNil(map) && !isNil(assetData)) {
      map.setView(assetData.centroid, assetData.zoom);
      const currentBounds = map.getBounds();
      const southWest = currentBounds.getSouthWest();
      const northEast = currentBounds.getNorthEast();
      map.setMaxBounds([
        [southWest.lat - 0.12, southWest.lng - 0.12],
        [northEast.lat + 0.12, northEast.lng + 0.12],
      ]);
    }
  }, [map, assetData]);

  return null;
};
const MapContent = React.memo(
  ({
    height = "40vh",
    width = "47vw",
    measureName,
    assetData,
    isFullscreen,
    setIsFullscreen,
    polylineElements,
    intl,
    equipmentData,
    percentiles,
    scales,
  }) => {
    return (
      <MapContainer
        id={`mapAnalysis_${measureName}`}
        center={assetData.centroid}
        zoom={assetData.zoom}
        style={{ width, height }}
        rotateControl={false}
      >
        <TileLayer
          url={window.RUNTIME_CONFIG.MAP_TYPES.find((type) => type.value === "regular").tileLayer}
          attribution={window.RUNTIME_CONFIG.IS_ONLINE ? "Google Maps" : "OpenStreetMap"}
          maxZoom={18}
          subdomains={["mt0", "mt1", "mt2", "mt3"]}
        />

        <BoundsFitter assetData={assetData} />

        {!isFullscreen && (
          <Button
            variant="contained"
            size="small"
            onClick={() => setIsFullscreen(true)}
            style={{
              position: "absolute",
              top: "19.3rem",
              right: "0.7rem",
              zIndex: 1000,
            }}
          >
            {intl.formatMessage({ id: "maximize" })}
          </Button>
        )}
        <Legend
          assetData={assetData}
          equipmentData={equipmentData}
          measureName={measureName}
          percentiles={percentiles}
          scales={scales}
          intl={intl}
        />

        {polylineElements}
      </MapContainer>
    );
  },
);

const MapAnalysis = ({
  measureName,
  assetData,
  spatialDataList,
  temporalDataList,
  intl,
  points,
  startIndex,
  endIndex,
  scales,
  percentiles,
  differences,
  equipmentData,
  dateTimeList,
  configData,
  assetModule,
}) => {
  const [loading, setLoading] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const equipmentTypes = useSelector((state) => state.equipmentTypeData.data);
  const [objectMeasures, setObjectMeasures] = useState(null);

  const differencesForTempLength = [
    { range: 5000, multiplier: 1 },
    { range: 15000, multiplier: 2 },
    { range: 25000, multiplier: 3 },
  ];

  useEffect(() => {
    if (!isNil(equipmentTypes)) {
      const newData = equipmentTypes.map((item) => {
        const measures = [];
        item.config?.measurands?.forEach((measure) => {
          if (measure.origin === "measure") {
            measures.push(measure.ref);
          }
        });
        return { name: item.name, measures };
      });
      setObjectMeasures(newData);
    }
  }, [equipmentTypes]);

  const { min, max } = useMemo(
    () => createColor({ assetData, measureName, percentiles, scales, equipmentData, intl }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const tColorScale = scales[measureName];

  const polylines = useMemo(() => {
    setLoading(true);

    if (!isNil(assetData) && !isNil(objectMeasures) && !isNil(differences) && !isNil(dateTimeList)) {
      const filteredPoints = [];

      for (let indexData = 0; indexData < dateTimeList.length; indexData++) {
        filteredPoints.push([]);
        const actualPoints = points[`${assetData.name}_${dateTimeList[indexData][2]}_${equipmentData.equipmentType}`];
        let temporalIndexes = [];
        if (!isEmpty(temporalDataList)) {
          temporalIndexes = temporalDataList.filter((x) => x.channel === dateTimeList[indexData][1]).map((x) => x.pk);
        }

        let prevValues = {
          state: null,
          values: spatialDataList?.[indexData]?.values?.[0],
          withColor: false,
        };

        let visualIndexes = clone(actualPoints.must_graph_points);
        if (!isEmpty(temporalIndexes) && !isNil(startIndex) && !isNil(endIndex)) {
          visualIndexes.push(startIndex, endIndex);
          visualIndexes = visualIndexes.filter((item) => item >= startIndex && item <= endIndex);
        }

        if (!isEmpty(actualPoints) && !isNil(actualPoints.points)) {
          let mult = 4;
          for (let indexDiff = 0; indexDiff < differencesForTempLength.length; indexDiff++) {
            if (actualPoints.points.length < differencesForTempLength[indexDiff].range) {
              mult = differencesForTempLength[indexDiff].multiplier;
            }
          }

          for (let i = 0; i < actualPoints.points.length; i++) {
            let p = clone(actualPoints.points[i]);
            if (BOOLEAN_MEASURES.includes(measureName)) {
              p["measuresValue"] = 0;
              for (
                let additionalValuesIndex = 0;
                additionalValuesIndex < spatialDataList?.[indexData]?.values?.length;
                additionalValuesIndex++
              ) {
                if (
                  spatialDataList?.[indexData]?.values?.[additionalValuesIndex][0] <=
                  i <=
                  spatialDataList?.[indexData]?.values?.[additionalValuesIndex][1]
                ) {
                  p["measuresValue"] = 1;
                }
              }
            } else {
              p["measuresValue"] = spatialDataList?.[indexData]?.values?.[i];
            }

            const differenceMultiplier = measureName === "temperature" ? 1 * mult : 6;
            let shouldAdd = false;

            if (!isEmpty(temporalIndexes)) {
              const poss = temporalIndexes.findIndex((t) => t === p.pk);
              if (poss === 0) {
                prevValues.values = p["measuresValue"];
                prevValues.withColor = true;
              }
              shouldAdd = shouldAddPoints({
                indexes: actualPoints.must_graph_points,
                index: i,
                pk: p.pk,
                difference: BOOLEAN_MEASURES.includes(measureName)
                  ? 1
                  : differences[measureName] * differenceMultiplier,
                value: measureName === "strain" ? Math.abs(p["measuresValue"]) : p["measuresValue"],
                temporalIndexes,
                prevValues,
                filteredPoints: filteredPoints[indexData],
                visualIndexes,
                startIndex,
                endIndex,
              });
            } else {
              shouldAdd = shouldAddPointsTotal({
                indexes: actualPoints.must_graph_points,
                index: i,
                pk: p.pk,
                difference: BOOLEAN_MEASURES.includes(measureName)
                  ? 1
                  : differences[measureName] * differenceMultiplier,
                value: p["measuresValue"],
                temporalIndexes,
                prevValues,
                filteredPoints: filteredPoints[indexData],
                visualIndexes,
              });
            }

            if (shouldAdd) {
              filteredPoints[indexData].push({ ...p, index: i, withColor: prevValues.withColor });
            }
          }
        }
      }

      const pList = dateTimeList.map((_, indexDTList) => {
        const list = [];
        for (let i = 0; i < filteredPoints[indexDTList].length - 1; i++) {
          const p1 = clone(filteredPoints[indexDTList][i]);
          const p2 = clone(filteredPoints[indexDTList][i + 1]);
          list.push({ p1, p2, withColor: p1.withColor });
        }
        return list;
      });

      setLoading(false);
      return pList;
    }

    if (isNil(dateTimeList) && !isNil(assetData) && !isNil(objectMeasures)) {
      const filteredPoints = [[]];
      const actualPoints = points;

      let prevValues = { state: null, values: 0.5, withColor: false };
      let temporalIndexes = [];
      if (!isEmpty(temporalDataList)) {
        temporalIndexes = temporalDataList.filter((x) => x.pk);
      }

      let visualIndexes = clone(actualPoints.must_graph_points);
      if (!isEmpty(temporalIndexes) && !isNil(startIndex) && !isNil(endIndex)) {
        visualIndexes.push(startIndex, endIndex);
        visualIndexes = visualIndexes.filter((item) => item >= startIndex && item <= endIndex);
      }

      if (!isEmpty(actualPoints) && !isNil(actualPoints.points)) {
        let mult = 4;
        for (let indexDiff = 0; indexDiff < differencesForTempLength.length; indexDiff++) {
          if (actualPoints.points.length < differencesForTempLength[indexDiff].range) {
            mult = differencesForTempLength[indexDiff].multiplier;
          }
        }

        for (let i = 0; i < actualPoints.points.length; i++) {
          let p = clone(actualPoints.points[i]);
          if (BOOLEAN_MEASURES.includes(measureName)) {
            p["measuresValue"] = 0;
            for (
              let additionalValuesIndex = 0;
              additionalValuesIndex < spatialDataList?.[0]?.values?.length;
              additionalValuesIndex++
            ) {
              if (
                spatialDataList?.[0]?.values?.[additionalValuesIndex][0] <=
                i <=
                spatialDataList?.[0]?.values?.[additionalValuesIndex][1]
              ) {
                p["measuresValue"] = 1;
              }
            }
          } else {
            p["measuresValue"] = spatialDataList?.[0]?.values?.[i];
          }

          const differenceMultiplier = measureName === "temperature" ? 1 * mult : 6;
          let shouldAdd = false;

          if (!isEmpty(temporalIndexes)) {
            const poss = temporalIndexes.findIndex((t) => t === p.pk);
            if (poss === 0) {
              prevValues.values = p["measuresValue"];
              prevValues.withColor = true;
            }
            shouldAdd = shouldAddPoints({
              indexes: actualPoints.must_graph_points,
              index: i,
              pk: p.pk,
              difference: BOOLEAN_MEASURES.includes(measureName) ? 1 : differences[measureName] * differenceMultiplier,
              value: measureName === "strain" ? Math.abs(p["measuresValue"]) : p["measuresValue"],
              temporalIndexes,
              prevValues,
              filteredPoints: filteredPoints[0],
              visualIndexes,
              startIndex,
              endIndex,
            });
          } else {
            shouldAdd = shouldAddPointsTotal({
              indexes: actualPoints.must_graph_points,
              index: i,
              pk: p.pk,
              difference: BOOLEAN_MEASURES.includes(measureName) ? 1 : differences[measureName] * differenceMultiplier,
              value: p["measuresValue"],
              temporalIndexes: [],
              prevValues,
              filteredPoints: filteredPoints[0],
              visualIndexes,
            });
          }

          if (shouldAdd) {
            filteredPoints[0].push({ ...p, index: i, withColor: prevValues.withColor });
          }
        }

        const pList = [[]];
        for (let i = 0; i < filteredPoints[0].length - 1; i++) {
          const p1 = clone(filteredPoints[0][i]);
          const p2 = clone(filteredPoints[0][i + 1]);
          pList[0].push({ p1, p2, withColor: p1.withColor });
        }
        setLoading(false);
        return pList;
      }
    }

    setLoading(false);
    return [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spatialDataList, temporalDataList]);

  const haveTemporalData = useMemo(
    () => !isEmpty(polylines) && !isEmpty(temporalDataList),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [polylines],
  );

  const polylineElements = useMemo(() => {
    if (isNil(tColorScale) || isEmpty(polylines)) return [];

    const elements = [];

    if (haveTemporalData) {
      if (!isNil(dateTimeList)) {
        for (let indexDTList = 0; indexDTList < dateTimeList.length; indexDTList++) {
          for (let i = 0; i < polylines[indexDTList].length; i++) {
            const segment = polylines[indexDTList][i];
            const weight = segment.withColor ? 10 : 5;
            const color = segment.withColor
              ? tColorScale(getMeasure(segment.p1.measuresValue, min, max, null, tColorScale.domain()) ?? 0)
              : "gray";

            const positions = [
              [segment.p1.latitude, segment.p1.longitude],
              [segment.p2.latitude, segment.p2.longitude],
            ];

            const handleClick =
              color !== "gray"
                ? () => {
                    const url = `https://www.google.com/maps/dir//${segment.p1.latitude},${segment.p1.longitude}`;
                    window.open(url, "_blank");
                  }
                : undefined;

            elements.push(
              <Polyline
                key={`${indexDTList}-${i}`}
                positions={positions}
                pathOptions={{ color, weight }}
                eventHandlers={handleClick ? { click: handleClick } : {}}
              />,
            );
          }
        }
      } else {
        for (let indexPoly = 0; indexPoly < polylines[0].length; indexPoly++) {
          const segment = polylines[0][indexPoly];
          const weight = segment.withColor ? 20 : 5;
          const color = segment.withColor
            ? tColorScale(getMeasure(segment.p1.measuresValue, min, max, null, tColorScale.domain()))
            : "gray";

          const positions = [
            [segment.p1.latitude, segment.p1.longitude],
            [segment.p2.latitude, segment.p2.longitude],
          ];

          const measurand = segment.p1?.measuresValue?.toFixed(2);
          const pk = segment.p1.pk.toFixed(2);
          const popupHtml = `
            <h3>${equipmentData.channels[spatialDataList[0].channel - 1].name} - ${spatialDataList[0].datetime.replace("T", " ")}</h3>
            <h4>PK: ${pk}</h4>
            <h4>${intl.formatMessage({ id: measureName })}: ${measurand}</h4>
          `;

          elements.push(
            <Polyline key={`t-${indexPoly}`} positions={positions} pathOptions={{ color, weight }}>
              <Popup>
                <div dangerouslySetInnerHTML={{ __html: popupHtml }} />
              </Popup>
            </Polyline>,
          );
        }
      }
    } else {
      if (!isNil(dateTimeList)) {
        const moduleData = configData.modules.find((module) => module.name === assetModule);
        const packageData = moduleData.packages.find((packg) => packg.name === measureName);

        const diffChannels = dateTimeList.map((dt) => equipmentData.channels[dt[1] - 1]);

        for (let indexDTList = 0; indexDTList < dateTimeList.length; indexDTList++) {
          for (let i = 0; i < polylines[indexDTList].length; i++) {
            const segment = polylines[indexDTList][i];
            const weight = 5;
            const color = tColorScale(getMeasure(segment.p1.measuresValue, min, max, null, tColorScale.domain()));

            const realIndex = contarHastaIndice(diffChannels, indexDTList) - 1;
            const length = contarHastaIndice(
              diffChannels,
              diffChannels.lastIndexOf(equipmentData.channels[dateTimeList[indexDTList][1] - 1]),
            );
            const asociado = asociarNumeroConLista(14, window.RUNTIME_CONFIG.lista);
            const m = (realIndex + 1) % 2 === 0 ? 1 : -1;
            const extra = length % 2 === 0 ? Math.round((realIndex + 1) / 2) : Math.round(realIndex / 2);
            const distance = asociado * m * extra;

            const pointIni = [polylines[indexDTList][0].p1.latitude, polylines[indexDTList][0].p1.longitude];
            const pointEnd = [
              polylines[indexDTList][polylines[indexDTList].length - 1].p2.latitude,
              polylines[indexDTList][polylines[indexDTList].length - 1].p2.longitude,
            ];
            const atanV1 = Math.atan2(pointEnd[0] - pointIni[0], pointEnd[1] - pointIni[1]);
            const offset_v1_latitude = length === 1 ? 0 : -distance * Math.cos(atanV1);
            const offset_v1_longitude = length === 1 ? 0 : distance * Math.sin(atanV1);

            const positions = [
              [segment.p1.latitude + offset_v1_latitude, segment.p1.longitude + offset_v1_longitude],
              [segment.p2.latitude + offset_v1_latitude, segment.p2.longitude + offset_v1_longitude],
            ];

            const measurand = segment.p1?.measuresValue?.toFixed(2);
            const pk = segment.p1.pk.toFixed(2);
            const popupHtml = `
              <h3>${equipmentData.channels[dateTimeList[indexDTList][1] - 1].name} - ${dateTimeList[indexDTList][0].replace("T", " ")}</h3>
              <h4>PK: ${pk}</h4>
              <h4>${intl.formatMessage({ id: measureName })}: ${measurand} ${packageData.unit}</h4>
            `;

            elements.push(
              <Polyline key={`dt-${indexDTList}-${i}`} positions={positions} pathOptions={{ color, weight }}>
                <Popup>
                  <div dangerouslySetInnerHTML={{ __html: popupHtml }} />
                </Popup>
              </Polyline>,
            );
          }
        }
      } else {
        if (!isNil(polylines) && !isEmpty(polylines)) {
          for (let indexPoly = 0; indexPoly < polylines[0].length; indexPoly++) {
            const segment = polylines[0][indexPoly];
            const weight = 5;
            const color = tColorScale(getMeasure(segment.p1.measuresValue, min, max, null, tColorScale.domain()));

            const positions = [
              [segment.p1.latitude, segment.p1.longitude],
              [segment.p2.latitude, segment.p2.longitude],
            ];

            const measurand = segment.p1?.measuresValue?.toFixed(2);
            const pk = segment.p1.pk.toFixed(2);
            const popupHtml = `
              <h3>${equipmentData.channels[spatialDataList[0].channel - 1].name} - ${spatialDataList[0].datetime.replace("T", " ")}</h3>
              <h4>PK: ${pk}</h4>
              <h4>${intl.formatMessage({ id: measureName })}: ${measurand}</h4>
            `;

            elements.push(
              <Polyline key={`nd-${indexPoly}`} positions={positions} pathOptions={{ color, weight }}>
                <Popup>
                  <div dangerouslySetInnerHTML={{ __html: popupHtml }} />
                </Popup>
              </Polyline>,
            );
          }
        }
      }
    }

    return elements;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [polylines, tColorScale, haveTemporalData]);

  if (isNil(polylines) || loading) {
    return (
      <div style={{ height: "100%", display: "flex", justifyContent: "center", alignItems: "center" }}>
        {`${intl.formatMessage({ id: "loading" })}...`}
      </div>
    );
  }

  return (
    <>
      <MapContent
        measureName={measureName}
        assetData={assetData}
        isFullscreen={isFullscreen}
        setIsFullscreen={setIsFullscreen}
        polylineElements={polylineElements}
        intl={intl}
        equipmentData={equipmentData}
        percentiles={percentiles}
        scales={scales}
      />

      <Dialog open={isFullscreen} maxWidth="xl" onClose={() => setIsFullscreen(false)}>
        <DialogContent style={{ height: "80vh", width: "80vw" }}>
          <MapContent
            measureName={measureName}
            assetData={assetData}
            isFullscreen={isFullscreen}
            setIsFullscreen={setIsFullscreen}
            polylineElements={polylineElements}
            intl={intl}
            equipmentData={equipmentData}
            percentiles={percentiles}
            scales={scales}
            width="100%"
            height="100%"
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setIsFullscreen(false)}>{intl.formatMessage({ id: "close" })}</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default MapAnalysis;
